// ============================================================
// PERRY — MAIN MENU
// New Game · Continue · Codex · Customize · Unstable Pierre · Options
//
// NEW GAME is destructive: it burns the Soul. For now that is ONE
// confirm, because caP'n is going to hit it forty times a day
// during testing. The two-step "type DELETE in caps" gate is
// written and waiting in the Roadmap for the day the game ships.
//
// What New Game does NOT touch: Kindling, Divine Crests, records, and
// achievements. Those are Empire-wide and outlive any run.
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { EmberDust, MetalRule, ensureKeyframes } from "./PerryUI";
import { SPECIES, GLYPH, deriveStanding, availablePoints } from "./perryData";

const KF = `
@keyframes menuIn    { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
@keyframes menuGlow  { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
@keyframes menuSweep { 0% { transform: translateX(-130%) skewX(-18deg); } 100% { transform: translateX(240%) skewX(-18deg); } }
`;

function MenuButton({ label, sub, onClick, disabled, danger, accent, index }) {
  const [hover, setHover] = useState(false);
  const tint = danger ? GM.rose : accent ? GM.amethystBright : GM.inkGoldBright;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        position: "relative", overflow: "hidden",
        width: "100%", textAlign: "left", padding: "13px 18px",
        marginBottom: 9, borderRadius: 10, cursor: disabled ? "default" : "pointer",
        background: hover && !disabled
          ? "linear-gradient(175deg, #2e1b0e 0%, #1c1009 60%, #120a08 100%)"
          : "linear-gradient(175deg, #1c1009 0%, #150c07 60%, #0d0705 100%)",
        border: `1px solid ${hover && !disabled ? tint : GM.pageBorder}`,
        boxShadow: hover && !disabled
          ? `0 8px 22px rgba(0,0,0,0.6), 0 0 26px ${tint}33, inset 0 1px 0 rgba(255,191,112,0.2)`
          : "0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(232,236,239,0.07)",
        opacity: disabled ? 0.35 : 1,
        transition: "all 0.16s ease",
        animation: `menuIn 0.5s ease-out ${0.05 * index}s backwards`,
      }}
    >
      {hover && !disabled && (
        <span style={{
          position: "absolute", top: 0, bottom: 0, width: "35%",
          background: "linear-gradient(90deg, transparent, rgba(255,214,160,0.11), transparent)",
          animation: "menuSweep 0.85s ease-out",
        }} />
      )}
      <div style={{
        fontFamily: GM.font, fontSize: 15, letterSpacing: 4,
        color: disabled ? GM.vellumFaint : hover ? "#ffe6c2" : GM.vellum,
        textShadow: hover && !disabled ? `0 0 14px ${tint}` : "0 1px 2px #000",
      }}>{label}</div>
      {sub && (
        <div style={{ fontSize: 10, letterSpacing: 1.5, color: GM.vellumFaint, marginTop: 3 }}>{sub}</div>
      )}
    </button>
  );
}

export default function MainMenu({
  soul, crests, kindling, standing, onNewGame, onContinue,
  onCodex, onCustomize, onOptions, onPierre, uid,
}) {
  ensureKeyframes("perry-menu-keyframes", KF);
  const [confirming, setConfirming] = useState(false);
  const sp = soul ? SPECIES[soul.species] : null;
  const points = soul ? availablePoints(soul) : 0;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !e.repeat) setConfirming(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      overflow: "auto", fontFamily: GM.fontUI,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={20} />

      <div style={{
        position: "relative", minHeight: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "40px 20px",
      }}>
        <div style={{ width: "min(430px, 92vw)" }}>
          {/* ── crest ── */}
          <div style={{ textAlign: "center", marginBottom: 26 }}>
            <div style={{
              fontFamily: GM.fontD, fontWeight: 700, fontSize: "clamp(46px, 11vw, 76px)",
              letterSpacing: 14, color: GM.vellum,
              textShadow: `0 0 48px ${GM.inkGold}, 0 4px 14px #000`,
            }}>PERRY</div>
            <MetalRule width="78%" style={{ margin: "8px auto 12px" }} />
            <div style={{ fontSize: 10, letterSpacing: 6, color: GM.vellumFaint }}>
              EVERY STRIKE HAS AN ANSWER
            </div>
          </div>

          {/* ── the traveller's card ── */}
          <div style={{
            marginBottom: 20, padding: "12px 16px", borderRadius: 10,
            background: "linear-gradient(175deg, rgba(43,184,168,0.05), rgba(0,0,0,0.35))",
            border: `1px solid ${soul ? GM.amethystDim : GM.pageBorder}`,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 12, letterSpacing: 2, color: GM.vellum }}>
                {soul ? (sp?.name || "Beb") : "No Soul kindled"}
              </div>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: GM.vellumFaint, marginTop: 2 }}>
                {soul ? `${standing || deriveStanding(soul)} · ${soul.renown || 0} Renown` : "Begin a New Game to choose what Perry is"}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: 14, color: GM.inkGoldBright, letterSpacing: 1,
                textShadow: `0 0 12px ${GM.inkGold}`,
              }}>{GLYPH.kd} {kindling ?? 0}</div>
              {points > 0 && (
                <div style={{
                  fontSize: 10, color: GM.amethystBright, marginTop: 2,
                  animation: "menuGlow 1.6s ease-in-out infinite",
                }}>{points} ASCENSION{points > 1 ? "S" : ""} READY</div>
              )}
            </div>
          </div>

          {/* ── the menu ── */}
          {!confirming ? (
            <>
              <MenuButton index={0} label="NEW GAME"
                sub={soul ? "Burns the current Soul — your purses and records survive" : "Choose what Perry is, and walk out of Reedbrook"}
                danger={!!soul}
                onClick={() => (soul ? setConfirming(true) : onNewGame())} />
              <MenuButton index={1} label="CONTINUE"
                sub={soul ? "Return to the road with everything you have earned" : "Nothing to return to yet"}
                disabled={!soul} onClick={onContinue} />
              <MenuButton index={2} label="CODEX"
                sub="Records · Achievements · Bestiary · Teachings · The Tale"
                onClick={onCodex} />
              {/* Relabelled 2026-07-27. This screen stopped being
                  "the constellation" the moment Whyllie's counter
                  moved into it — you can now spend Renown AND ⋔
                  from here, without having survived to Hollowmere
                  first. The sub-line says whichever you have. */}
              <MenuButton index={3} label="KIT UP"
                sub={
                  points > 0
                    ? `Constellation · Armoury · Wares — ${points} point${points > 1 ? "s" : ""} and ${GLYPH.kd} ${kindling ?? 0} to spend`
                    : `Constellation · Armoury · Wares — ${GLYPH.kd} ${kindling ?? 0} to spend`
                }
                disabled={!soul} accent={points > 0 || (kindling ?? 0) > 0} onClick={onCustomize} />
              <MenuButton index={4} label="UNSTABLE PIERRE"
                sub={uid ? "Step through the portal to the Pier" : "Sign in to travel"}
                accent disabled={!uid} onClick={onPierre} />
              <MenuButton index={5} label="OPTIONS" sub="Presentation · keys · credits" onClick={onOptions} />
            </>
          ) : (
            <div style={{
              padding: 20, borderRadius: 12,
              background: "linear-gradient(175deg, #2a0f0c, #150807)",
              border: `1px solid ${GM.roseDim}`,
              animation: "menuIn 0.3s ease-out",
            }}>
              <div style={{
                fontFamily: GM.font, fontSize: 16, letterSpacing: 3, color: GM.rose,
                marginBottom: 10,
              }}>BURN THE SOUL?</div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: GM.vellumDim, marginBottom: 8 }}>
                Your species, your constellation, your Anima, your wares — all of it, gone at once.
                That is how a Soul is lost in this Empire: never in pieces.
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6, color: GM.amethyst, marginBottom: 18 }}>
                Your Kindling, your Divine Crests, your records and your achievements are NOT touched.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setConfirming(false); onNewGame(); }} style={{
                  flex: 1, padding: "11px 0", borderRadius: 9, cursor: "pointer",
                  background: "linear-gradient(175deg, #8e3a30, #5a1e18)",
                  border: `1px solid ${GM.rose}`, color: "#ffdad4",
                  fontFamily: GM.font, letterSpacing: 3, fontSize: 13,
                }}>BURN IT</button>
                <button onClick={() => setConfirming(false)} style={{
                  flex: 1, padding: "11px 0", borderRadius: 9, cursor: "pointer",
                  background: "linear-gradient(175deg, #1c1009, #0d0705)",
                  border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
                  fontFamily: GM.font, letterSpacing: 3, fontSize: 13,
                }}>KEEP IT</button>
              </div>
            </div>
          )}

          <div style={{
            marginTop: 22, textAlign: "center", fontSize: 9,
            letterSpacing: 3, color: GM.vellumFaint, opacity: 0.7,
          }}>
            AN ILLEGAL EMPIRE TITLE · BEBS SHOULD NOT BE SLEPT ON
          </div>
        </div>
      </div>
    </div>
  );
}
